// Third Party Imports
import {createSelector} from 'reselect';
import get from 'lodash/get';

// Relative Imports
import {getTranslations, getListingExtended} from './store';
import {getIsMobile, getLocale, getRequestMarker} from './system';
import {getSearchUrl, getSearchBaseUrl} from './searchUrl';
import {
    getListingByOwnerProperties,
    getListingRecommendedProperties,
    getListingInquiryProperties,
    getPreferredLocation
} from './listing';
import {labelMaker} from '../../server/utils/labelMaker';
import {getOwnerDetails} from './ownerDetails';

// GraphQl impl
const getLocation = (hit) => {
    const city = get(hit, 'listing.address.city', '');
    const stateProv = get(hit, 'listing.address.stateProvince');
    return city + (stateProv ? `, ${stateProv}` : '');
};

const getAccomodations = (hit, translations) => {
    let result = '';
    const bdCount = get(hit, 'listing.bedrooms');
    if (bdCount) {
        result += `${bdCount}${translations.bedroomsAbbreviation}`;
    }
    const sleepsCount = get(hit, 'listing.sleeps');
    if (sleepsCount) {
        if (result !== '') {
            result += ' · ';
        }
        result += `${translations.sleeps} ${sleepsCount}`;
    }
    return result;
};

const getAvgNightlyRate = (hit, translations) => {
    const averageNightly = get(hit, 'listing.averagePrice.localized');
    const label = labelMaker(translations.seoAveragePrice, {
        averagePrice: averageNightly
    });
    return averageNightly ? label : '';
};

const getRatingDetails = (hit) => {
    return {
        averageRating: get(hit, 'listing.averageRating', 0),
        reviewCount: get(hit, 'listing.reviewCount', 0)
    };
};

const hasHits = (hits) => (hits ? hits && hits.length : false);

const replaceImageTreatment = (isMobile, hit) => {
    let imageUrl = get(hit, 'listing.thumbnailUrl');
    return imageUrl ? imageUrl.replace('TREATMENT', isMobile ? 'c2' : 'c4') : '';
};

const fillHitLabels = (isMobile, properties, translations) => {
    return properties.map((hit) => {
        return {
            location: getLocation(hit),
            accomodations: getAccomodations(hit, translations),
            avgNightlyRate: getAvgNightlyRate(hit, translations),
            ratings: getRatingDetails(hit),
            imageUrl: replaceImageTreatment(isMobile, hit),
            propertyHref: get(hit, 'listing.detailPageUrl'),
            headline: get(hit, 'listing.propertyMetadata.headline'),
            spu: get(hit, 'listing.spu')
        };
    });
};

const getGeoTitle = createSelector(
    [getPreferredLocation, getTranslations],
    (preferredLocation, translations) => {
        const geoString = get(preferredLocation, 'name.simple');
        return labelMaker(translations.morePropertiesIn, geoString);
    }
);

// REST and GraphQl
const getRecommendedLoadingState = createSelector(
    [getListingExtended, getListingRecommendedProperties],
    (listingExtended, listingRecommendedProperties) => {
        const {
            recommendedPropertiesIsFetching: isLoading,
            recommendedPropertiesFetchFailed: fetchFailed,
            recommendedPropertiesFetchSuccess: fetchSuccess
        } = listingExtended;

        const hits = hasHits(listingRecommendedProperties);

        return {
            isLoading,
            isEmptyOrFailed: fetchFailed || (fetchSuccess && !hits)
        };
    }
);

const getRecommendedPropertiesThumbnails = createSelector(
    [getIsMobile, getListingRecommendedProperties],
    (isMobile, listingRecommendedProperties = []) => {
        const numHits = listingRecommendedProperties.length;
        const targetLength = 5;
        let thumbnails = [];

        if (numHits) {
            // ensure there are 5 images, repeat thumbnails if needed
            thumbnails = [...new Array(targetLength).keys()].map((val, i) => {
                const hit = listingRecommendedProperties[i]
                    ? listingRecommendedProperties[i]
                    : listingRecommendedProperties[i % numHits];

                return replaceImageTreatment(isMobile, hit);
            });
        }

        return thumbnails;
    }
);

const getRecommendedProperties = createSelector(
    [
        getIsMobile,
        getTranslations,
        getRecommendedLoadingState,
        getListingRecommendedProperties,
        getRecommendedPropertiesThumbnails,
        getLocale,
        getSearchUrl,
        getSearchBaseUrl,
        getGeoTitle,
        getRequestMarker
    ],
    (
        isMobile,
        translations,
        loadingState,
        listingRecommendedProperties,
        recommendedPropertiesThumbnails,
        locale,
        searchUrl,
        searchUrlBase,
        geoTitle,
        requestMarker
    ) => {
        return {
            ...loadingState,
            recommendedHits:
                listingRecommendedProperties && listingRecommendedProperties.map
                    ? fillHitLabels(isMobile, listingRecommendedProperties, translations)
                    : [],
            recommendedPropertiesThumbnails,
            translations: {
                recommendationMessage: translations.recommendationMessage,
                rpNoReviews: translations.rpNoReviews,
                sleeps: translations.sleeps,
                bedroomsAbbreviation: translations.bedroomsAbbreviation,
                averageNightly: translations.averageNightly,
                seeMore: translations.seeMore,
                seeMoreProperties: translations.seeMoreProperties,
                studio: translations.studio,
                propertiesInThisGeo: geoTitle,
                viewMore: translations.more
            },
            searchUrl,
            searchUrlBase,
            locale,
            requestMarker
        };
    }
);

const getByOwnerLoadingState = createSelector(
    [getListingExtended, getListingByOwnerProperties],
    (listingExtended, listingByOwnerProperties) => {
        const {
            byOwnerPropertiesFetchFailed: fetchFailed,
            byOwnerPropertiesFetchSuccess: fetchSuccess
        } = listingExtended;

        const hits = hasHits(listingByOwnerProperties);

        return {
            isLoading: !fetchFailed && !fetchSuccess,
            isEmptyOrFailed: fetchFailed || (fetchSuccess && !hits)
        };
    }
);

const getByOwnerProperties = createSelector(
    [
        getIsMobile,
        getTranslations,
        getByOwnerLoadingState,
        getListingByOwnerProperties,
        getLocale,
        getOwnerDetails,
        getRequestMarker
    ],
    (
        isMobile,
        translations,
        loadingState,
        listingByOwnerProperties,
        locale,
        ownerDetails,
        requestMarker
    ) => {
        return {
            ...loadingState,
            recommendedHits:
                listingByOwnerProperties && listingByOwnerProperties.map
                    ? fillHitLabels(isMobile, listingByOwnerProperties, translations)
                    : [],
            translations: {
                byOwnerMessage: translations.byOwnerMessage,
                rpNoReviews: translations.rpNoReviews,
                sleeps: translations.sleeps,
                bedroomsAbbreviation: translations.bedroomsAbbreviation,
                averageNightly: translations.averageNightly,
                studio: translations.studio
            },
            ownerDetails,
            locale,
            requestMarker
        };
    }
);

const getInquiryLoadingState = createSelector(
    [getListingExtended, getListingInquiryProperties],
    (listingExtended, listingInquiryProperties) => {
        const {
            inquiryPropertiesFetchFailed: fetchFailed,
            inquiryPropertiesFetchSuccess: fetchSuccess
        } = listingExtended;

        const hits = hasHits(listingInquiryProperties);

        return {
            isLoading: !fetchFailed && !fetchSuccess,
            isEmptyOrFailed: fetchFailed || (fetchSuccess && !hits)
        };
    }
);

const getInquiryProperties = createSelector(
    [
        getIsMobile,
        getTranslations,
        getInquiryLoadingState,
        getListingInquiryProperties,
        getLocale,
        getSearchUrl,
        getSearchBaseUrl,
        getGeoTitle,
        getRequestMarker
    ],
    (
        isMobile,
        translations,
        loadingState,
        listingInquiryProperties,
        locale,
        searchUrl,
        searchUrlBase,
        geoTitle,
        requestMarker
    ) => {
        return {
            ...loadingState,
            recommendedHits:
                listingInquiryProperties && listingInquiryProperties.map
                    ? fillHitLabels(isMobile, listingInquiryProperties, translations)
                    : [],
            translations: {
                recommendationMessage: translations.recommendationMessage,
                rpNoReviews: translations.rpNoReviews,
                sleeps: translations.sleeps,
                bedroomsAbbreviation: translations.bedroomsAbbreviation,
                averageNightly: translations.averageNightly,
                seeMore: translations.seeMore,
                seeMoreProperties: translations.seeMoreProperties,
                studio: translations.studio,
                propertiesInThisGeo: geoTitle,
                viewMore: translations.more
            },
            searchUrl,
            searchUrlBase,
            locale,
            requestMarker
        };
    }
);

export {
    getRecommendedProperties,
    getByOwnerProperties,
    getInquiryProperties,
    getRecommendedLoadingState,
    getRecommendedPropertiesThumbnails,
    getByOwnerLoadingState,
    getInquiryLoadingState,
    fillHitLabels,
    getGeoTitle,
    hasHits
};
